import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useUserData } from '../hooks/useUserData';
import { useAuth } from '../contexts/AuthContext';
import { FirestoreService } from '../services/firestoreService';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Switch,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  TextField,
  MenuItem,
  Tab,
  Tabs,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  LinearProgress,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Language,
  DarkMode,
  Email,
  Sms,
  NotificationsActive,
  CreditCard,
  Delete,
  Edit,
  Backup,
  Download,
  Share,
  Help,
  Info,
  Logout,
  Support,
  PhotoCamera,
  Add,
} from '@mui/icons-material';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.8)'
    : 'rgba(15, 23, 42, 0.8)',
  backdropFilter: 'blur(10px)',
}));

const ProfileCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -50,
    right: -50,
    width: 150,
    height: 150,
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const Settings: React.FC = () => {
  const { user } = useAuth();
  const { mode: themeMode, savedMode, toggleTheme, setThemeMode, saveThemeToDatabase, hasUnsavedThemeChanges } = useTheme();
  const { 
    profile, 
    preferences, 
    loading: userDataLoading, 
    updateProfile, 
    updatePreference,
    saveProfile,
    savePreferences 
  } = useUserData();
  
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Track original theme mode to detect changes
  const [originalThemeMode, setOriginalThemeMode] = useState(themeMode);
  
  const [notifications, setNotifications] = useState({
    email: true,
    sms: false,
    push: true,
    deals: true,
    expiry: true,
    transfers: false,
  });
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [exportDataDialog, setExportDataDialog] = useState(false);
  
  // Notification state for success/error messages
  const [notificationState, setNotificationState] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  
  // Loading state for save operations
  const [isSaving, setIsSaving] = useState(false);
  
  // Local state for editing profile (separate from main profileData)
  const [editingProfile, setEditingProfile] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
  });
  const [profileData, setProfileData] = useState({
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '+91 98765 43210',
    dateOfBirth: '1990-06-15',
    address: '123 Main Street, Mumbai, India',
    memberSince: '2022',
    totalPoints: 125480,
    programsConnected: 8,
    tier: 'Gold Member',
    avatar: '/api/placeholder/80/80',
    profileCompletion: 85,
  });

  // Sync local states with Firestore data when it loads
  useEffect(() => {
    if (preferences) {
      console.log('🔥 Settings: Syncing preferences data from Firestore:', preferences);
      setLocalNotifications(prev => ({
        ...prev,
        ...preferences.notifications || {}
      }));
      setLocalPreferences(prev => ({
        ...prev,
        // Add other preference fields here if they exist in Firestore
      }));
    }
  }, [preferences]);

  // Track theme changes for save button (now unnecessary since themes save immediately)
  useEffect(() => {
    // Theme changes are now saved immediately, no tracking needed
  }, [themeMode, originalThemeMode]);

  // Debug effect to log profileData changes
  useEffect(() => {
    console.log('🔥 Settings: profileData state changed:', profileData);
  }, [profileData]);

  // Sync with Firestore data when it loads
  useEffect(() => {
    console.log('🔥 Settings: useEffect triggered, profile:', profile);
    if (profile) {
      console.log('🔥 Settings: Syncing profile data from Firestore:', profile);
      const newProfileData = {
        ...profileData,
        name: profile.name || 'John Doe',
        email: profile.email || 'john.doe@example.com',
        phone: profile.phone || '+91 98765 43210',
        dateOfBirth: profile.dateOfBirth || '1990-06-15',
        address: profile.address || '123 Main Street, Mumbai, India',
        avatar: profile.avatar || profileData.avatar,
      };
      console.log('🔥 Settings: New profile data to set:', newProfileData);
      setProfileData(newProfileData);
      console.log('✅ Settings: Profile data synced to UI');
    } else {
      console.log('📝 Settings: No profile data from Firestore, using defaults');
    }
  }, [profile]);

  // Sync notifications from Firestore preferences (theme is now handled by ThemeContext)
  useEffect(() => {
    if (preferences) {
      if (preferences.notifications) {
        setNotifications(preferences.notifications);
      }
    }
  }, [preferences]);

  const [localPreferences, setLocalPreferences] = useState({
    currency: 'USD',
    language: 'en',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    emailFrequency: 'weekly',
    autoSync: true,
  });

  // Track changes separately (theme saves automatically)
  const [notificationsChanged, setNotificationsChanged] = useState(false);
  const [profileChanged, setProfileChanged] = useState(false);
  const [preferencesChanged, setPreferencesChanged] = useState(false);

  // Track if there are unsaved changes (excluding theme which saves automatically)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Local state for all editable settings (without auto-save)
  const [localNotifications, setLocalNotifications] = useState({
    email: true,
    sms: false,
    push: true,
    deals: true,
    expiry: true,
    transfers: false,
  });

  // Cleanup effect: No longer needed since themes save immediately
  useEffect(() => {
    // Theme changes now save immediately, no cleanup needed
  }, [preferences, setThemeMode]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  // Update handlers to track changes locally without auto-saving
  const handleNotificationChange = (type: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalNotifications(prev => ({
      ...prev,
      [type]: event.target.checked,
    }));
    setNotificationsChanged(true);
    console.log('🔥 Settings: Notification preference changed locally (not saved yet):', type, event.target.checked);
  };

  const handlePreferenceChange = (key: string, value: any) => {
    setLocalPreferences(prev => ({
      ...prev,
      [key]: value,
    }));
    setPreferencesChanged(true);
    console.log('🔥 Settings: Preference changed locally (not saved yet):', key, value);
  };

  const handleProfileChange = (field: string, value: any) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value,
    }));
    setProfileChanged(true);
    console.log('🔥 Settings: Profile field changed locally (not saved yet):', field, value);
  };

  // Update the hasUnsavedChanges calculation (including theme changes)
  useEffect(() => {
    const hasChanges = hasUnsavedThemeChanges || notificationsChanged || profileChanged || preferencesChanged;
    setHasUnsavedChanges(hasChanges);
  }, [hasUnsavedThemeChanges, notificationsChanged, profileChanged, preferencesChanged]);

  // Theme toggle handler - changes theme locally, activates save button
  const handleThemeToggle = () => {
    toggleTheme(); // Changes theme immediately but doesn't save to database
    console.log('🔥 Settings: Theme changed locally, save button will activate');
  };

  // Comprehensive save function for all changes
  const handleSaveAllChanges = async () => {
    try {
      console.log('🔥 Settings: Starting comprehensive save of all changes');
      setIsSaving(true);

      // Save theme changes if they exist
      if (hasUnsavedThemeChanges) {
        console.log('🎨 Settings: Saving theme changes to database');
        await saveThemeToDatabase();
      }

      // Prepare profile updates
      const profileUpdates: any = {
        name: profileData.name,
        email: profileData.email,
        phone: profileData.phone,
        dateOfBirth: profileData.dateOfBirth,
        address: profileData.address,
      };

      // Prepare preferences updates
      const preferencesUpdates = {
        notifications: {
          email: localNotifications.email,
          sms: localNotifications.sms,
          push: localNotifications.push,
          deals: localNotifications.deals,
          expiry: localNotifications.expiry,
          transfers: localNotifications.transfers,
        },
        privacy: {
          dataCollection: false,
          personalization: true,
          marketing: false,
          thirdParty: false,
        },
        theme: themeMode, // Use current global theme mode
      };

      console.log('🔥 Settings: Profile updates to save:', profileUpdates);
      console.log('🔥 Settings: Preferences updates to save:', preferencesUpdates);

      // Save both profile and preferences in parallel
      await Promise.all([
        updateProfile(profileUpdates),
        FirestoreService.saveUserPreferences(user!.id, preferencesUpdates)
      ]);

      console.log('✅ Settings: All changes saved successfully');
      
      // Reset all change flags
      setHasUnsavedChanges(false);
      setNotificationsChanged(false);
      setProfileChanged(false);
      setPreferencesChanged(false);
      // Theme changes are handled immediately, no flag to reset
      
      // Show success notification
      setNotificationState({
        open: true,
        message: 'All settings updated successfully!',
        severity: 'success'
      });

    } catch (error) {
      console.error('❌ Settings: Failed to save all changes:', error);
      
      // Show error notification
      setNotificationState({
        open: true,
        message: 'Failed to save settings. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const avatarData = e.target?.result as string;
        const newProfileData = { 
          ...profileData, 
          avatar: avatarData 
        };
        setProfileData(newProfileData);
        
        try {
          // Save to Firestore
          await updateProfile({
            name: newProfileData.name,
            email: newProfileData.email,
            phone: newProfileData.phone,
            dateOfBirth: newProfileData.dateOfBirth,
            address: newProfileData.address,
            avatar: avatarData,
          });
        } catch (error) {
          console.error('Failed to save avatar:', error);
          // Revert local state on error
          setProfileData(profileData);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleExportData = async (format: 'json' | 'csv') => {
    // Simulate data export
    const data = {
      profile: profileData,
      loyaltyPrograms: connectedAccounts,
      preferences,
      exportDate: new Date().toISOString(),
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { 
      type: format === 'json' ? 'application/json' : 'text/csv' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `loyalty-data-${new Date().toISOString().split('T')[0]}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setExportDataDialog(false);
  };

  const connectedAccounts = [
    { id: 'chase', name: 'Chase Sapphire', type: 'Credit Card', status: 'Active', lastSync: '2 hours ago' },
    { id: 'marriott', name: 'Marriott Bonvoy', type: 'Hotel Program', status: 'Active', lastSync: '1 day ago' },
    { id: 'emirates', name: 'Emirates Skywards', type: 'Airline Program', status: 'Inactive', lastSync: '1 week ago' },
    { id: 'hdfc', name: 'HDFC Infinia', type: 'Credit Card', status: 'Active', lastSync: '3 hours ago' },
  ];

  return (
    <>
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: 'text.primary' }}>
          Settings
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3 }}>
          Manage your account preferences and app configuration
        </Typography>
      </Box>

      {/* Profile Overview */}
      <ProfileCard sx={{ mb: 4 }}>
        <CardContent sx={{ position: 'relative', zIndex: 2, p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={profileData.avatar}
                sx={{ width: 80, height: 80 }}
              />
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="profile-photo-upload"
                type="file"
                onChange={handlePhotoUpload}
              />
              <label htmlFor="profile-photo-upload">
                <Button
                  component="span"
                  sx={{
                    position: 'absolute',
                    bottom: -5,
                    right: -5,
                    minWidth: 32,
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    },
                  }}
                >
                  <PhotoCamera sx={{ fontSize: 16, color: 'white' }} />
                </Button>
              </label>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {profileData.name}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                {profileData.email}
              </Typography>
              
              {/* Profile Completion */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Profile Completion
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {profileData.profileCompletion}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={profileData.profileCompletion} 
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: 'white',
                      borderRadius: 3,
                    }
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Chip 
                  label={profileData.tier} 
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                    color: 'white',
                    fontWeight: 600,
                  }} 
                />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Member since {profileData.memberSince}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {profileData.totalPoints.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Total Points
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                {profileData.programsConnected} programs connected
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </ProfileCard>

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={selectedTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
            },
          }}
        >
          <Tab label="Account" />
          <Tab label="Notifications" />
          <Tab label="Connected Programs" />
          <Tab label="Data & Support" />
        </Tabs>
      </Box>

      {/* Account Settings Tab */}
      <TabPanel value={selectedTab} index={0}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Personal Information
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Full Name"
                  value={profileData.name}
                  onChange={(e) => handleProfileChange('name', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Email Address"
                  value={profileData.email}
                  onChange={(e) => handleProfileChange('email', e.target.value)}
                  fullWidth
                  type="email"
                />
                <TextField
                  select
                  label="Preferred Currency"
                  value={localPreferences.currency}
                  onChange={(e) => handlePreferenceChange('currency', e.target.value)}
                  fullWidth
                  helperText="Used for displaying points values and redemption rates"
                >
                  <MenuItem value="USD">🇺🇸 USD - US Dollar</MenuItem>
                  <MenuItem value="EUR">🇪🇺 EUR - Euro</MenuItem>
                  <MenuItem value="GBP">🇬🇧 GBP - British Pound</MenuItem>
                  <MenuItem value="INR">🇮🇳 INR - Indian Rupee</MenuItem>
                  <MenuItem value="AED">🇦🇪 AED - UAE Dirham</MenuItem>
                  <MenuItem value="CAD">🇨🇦 CAD - Canadian Dollar</MenuItem>
                  <MenuItem value="AUD">🇦🇺 AUD - Australian Dollar</MenuItem>
                  <MenuItem value="SGD">🇸🇬 SGD - Singapore Dollar</MenuItem>
                </TextField>
              </Box>
            </CardContent>
          </StyledCard>

          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                App Preferences
              </Typography>
              
              <List sx={{ p: 0 }}>
                <ListItem>
                  <ListItemIcon>
                    <DarkMode />
                  </ListItemIcon>
                  <ListItemText
                    primary="Dark Mode"
                    secondary={
                      hasUnsavedThemeChanges 
                        ? `Theme changed to ${themeMode} mode. Click 'Save All Changes' to persist.`
                        : "Switch between light and dark themes"
                    }
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={themeMode === 'dark'}
                      onChange={handleThemeToggle}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </StyledCard>
        </Box>
      </TabPanel>

      {/* Notifications Tab */}
      <TabPanel value={selectedTab} index={1}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Communication Preferences
              </Typography>
              
              <List sx={{ p: 0 }}>
                <ListItem>
                  <ListItemIcon>
                    <Email />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email Notifications"
                    secondary="Receive notifications via email"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={localNotifications.email}
                      onChange={handleNotificationChange('email')}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Sms />
                  </ListItemIcon>
                  <ListItemText
                    primary="SMS Notifications"
                    secondary="Receive notifications via text message"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={localNotifications.sms}
                      onChange={handleNotificationChange('sms')}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <NotificationsActive />
                  </ListItemIcon>
                  <ListItemText
                    primary="Push Notifications"
                    secondary="Receive push notifications on your device"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={localNotifications.push}
                      onChange={handleNotificationChange('push')}
                    />
                  </ListItemSecondaryAction>
              </ListItem>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, px: 2 }}>
                Notification Types
              </Typography>
              
              <ListItem>
                <ListItemText
                  primary="Deals & Offers"
                  secondary="Get notified about new deals and promotional offers"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={notifications.deals}
                    onChange={handleNotificationChange('deals')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem>
                <ListItemText
                  primary="Points Expiry"
                  secondary="Alerts when your points are about to expire"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={notifications.expiry}
                    onChange={handleNotificationChange('expiry')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem>
                <ListItemText
                  primary="Transfer Updates"
                  secondary="Notifications about point transfers and transactions"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={notifications.transfers}
                    onChange={handleNotificationChange('transfers')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, px: 2 }}>
                Email Frequency
              </Typography>
              
              <Box sx={{ px: 2 }}>
                <TextField
                  select
                  label="Email Frequency"
                  value={localPreferences.emailFrequency}
                  onChange={(e) => handlePreferenceChange('emailFrequency', e.target.value)}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="immediate">Immediate</MenuItem>
                  <MenuItem value="daily">Daily Digest</MenuItem>
                  <MenuItem value="weekly">Weekly Summary</MenuItem>
                  <MenuItem value="monthly">Monthly Report</MenuItem>
                  <MenuItem value="never">Never</MenuItem>
                </TextField>
              </Box>
            </List>
          </CardContent>
        </StyledCard>
        
        <StyledCard>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              Notification History
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Recent notifications from the last 30 days
            </Alert>
            
            <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
              {[
                { type: 'deal', message: 'New 50% bonus on Chase transfers', time: '2 hours ago', status: 'unread' },
                { type: 'expiry', message: 'American Express points expiring in 30 days', time: '1 day ago', status: 'read' },
                { type: 'transfer', message: 'Transfer to Marriott completed', time: '3 days ago', status: 'read' },
                { type: 'deal', message: 'Limited time: Double points at hotels', time: '1 week ago', status: 'read' },
              ].map((notification, index) => (
                <Box key={index} sx={{ 
                  p: 2, 
                  border: 1, 
                  borderColor: 'divider', 
                  borderRadius: 1, 
                  mb: 1,
                  backgroundColor: notification.status === 'unread' ? 'action.hover' : 'transparent',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Box>
                    <Typography variant="body2" sx={{ 
                      fontWeight: notification.status === 'unread' ? 600 : 400 
                    }}>
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {notification.time}
                    </Typography>
                  </Box>
                  {notification.status === 'unread' && (
                    <Chip size="small" label="New" color="primary" />
                  )}
                </Box>
              ))}
            </Box>
          </CardContent>
        </StyledCard>
        </Box>
      </TabPanel>

      {/* Connected Programs Tab */}
      <TabPanel value={selectedTab} index={2}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Connected Programs ({connectedAccounts.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  onClick={() => alert('Add new loyalty program coming soon!')}
                >
                  Add Program
                </Button>
              </Box>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                Auto-sync is enabled for all connected programs. Last sync: 2 hours ago
              </Alert>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  { 
                    name: 'Chase Sapphire', 
                    type: 'Credit Card',
                    membershipId: '****1234',
                    points: 125420,
                    status: 'Connected',
                    lastSync: '2 hours ago',
                    color: '#1976d2',
                    logo: null
                  },
                  { 
                    name: 'Marriott Bonvoy', 
                    type: 'Hotel Program',
                    membershipId: 'MB****5678',
                    points: 85630,
                    status: 'Connected',
                    lastSync: '1 day ago',
                    color: '#8B0000',
                    logo: null
                  },
                  { 
                    name: 'Emirates Skywards', 
                    type: 'Airline Program',
                    membershipId: 'EK****9012',
                    points: 45280,
                    status: 'Needs Attention',
                    lastSync: '1 week ago',
                    color: '#C41E3A',
                    logo: null
                  },
                  { 
                    name: 'HDFC Infinia', 
                    type: 'Credit Card',
                    membershipId: '****3456',
                    points: 92150,
                    status: 'Connected',
                    lastSync: '3 hours ago',
                    color: '#FF6F00',
                    logo: null
                  },
                ].map((account, index) => (
                  <Box key={index}>
                    <Box sx={{ 
                      p: 3, 
                      border: 1, 
                      borderColor: 'divider', 
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}>
                      <Avatar 
                        src={account.logo || undefined} 
                        sx={{ 
                          width: 48, 
                          height: 48,
                          backgroundColor: account.color,
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      >
                        {account.name[0]}
                      </Avatar>
                      
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {account.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {account.membershipId} • {account.points.toLocaleString()} points
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {account.type} • Last updated: {account.lastSync}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip 
                          label={account.status} 
                          color={account.status === 'Connected' ? 'success' : 'warning'}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                        <br />
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => alert(`Manage ${account.name} connection`)}
                        >
                          Manage
                        </Button>
                      </Box>
                    </Box>
                    {index < 3 && <Divider />}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </StyledCard>
          
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Program Sync & Management
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Last Successful Sync
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    2 hours ago - All programs
                  </Typography>
                </Box>
                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Auto-Sync Frequency
                  </Typography>
                  <Typography variant="body2">
                    Every 4 hours
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => alert('Syncing all programs...')}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Sync All Programs Now
                </Button>
                
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => alert('Running connection test...')}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Test All Connections
                </Button>
              </Box>
              
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Having sync issues? Try testing connections first. 
                  Contact support if problems persist.
                </Typography>
              </Alert>
            </CardContent>
          </StyledCard>
        </Box>
      </TabPanel>

      {/* Data & Support Tab */}
      <TabPanel value={selectedTab} index={3}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Data Export & Backup
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Storage Used
                </Typography>
                <LinearProgress variant="determinate" value={45} sx={{ mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  4.5 MB of 10 MB used
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => setExportDataDialog(true)}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  fullWidth
                >
                  Export Loyalty Data
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<Backup />}
                  onClick={() => alert('Backup created successfully!')}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  fullWidth
                >
                  Create Data Backup
                </Button>
              </Box>
              
              <Alert severity="info">
                <Typography variant="body2">
                  Export includes: Points balances, program connections, transaction history, and preferences.
                </Typography>
              </Alert>
            </CardContent>
          </StyledCard>

          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Help & Support
              </Typography>
              
              <List sx={{ p: 0 }}>
                <ListItem 
                  component="button"
                  onClick={() => alert('Opening help center...')}
                  sx={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}
                >
                  <ListItemIcon>
                    <Help />
                  </ListItemIcon>
                  <ListItemText
                    primary="Help Center"
                    secondary="Find answers to common questions"
                  />
                </ListItem>
                
                <ListItem 
                  component="button"
                  onClick={() => alert('Connecting you with support...')}
                  sx={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}
                >
                  <ListItemIcon>
                    <Support />
                  </ListItemIcon>
                  <ListItemText
                    primary="Contact Support"
                    secondary="Get help with your account"
                  />
                </ListItem>
                
                <ListItem 
                  component="button"
                  onClick={() => alert('App version 2.1.4')}
                  sx={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}
                >
                  <ListItemIcon>
                    <Info />
                  </ListItemIcon>
                  <ListItemText
                    primary="About PoinVoy"
                    secondary="Version 2.1.4 • Built for loyalty enthusiasts"
                  />
                </ListItem>
                
                <ListItem 
                  component="button"
                  onClick={() => alert('Sign out confirmation...')}
                  sx={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}
                >
                  <ListItemIcon>
                    <Logout />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sign Out"
                    secondary="Sign out of your account"
                  />
                </ListItem>
              </List>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Your Data Overview
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Connected Programs</Typography>
                    <Typography variant="body2" fontWeight={600}>4 active</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Tracked Transactions</Typography>
                    <Typography variant="body2" fontWeight={600}>127 records</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Total Points Value</Typography>
                    <Typography variant="body2" fontWeight={600} color="success.main">$3,247</Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Box>
      </TabPanel>

      {/* Global Save Changes Section */}
      <Box sx={{ 
        position: 'sticky', 
        bottom: 0, 
        bgcolor: 'background.paper', 
        borderTop: 1, 
        borderColor: 'divider',
        p: 3,
        mt: 4,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {hasUnsavedChanges && (
            <Alert severity="warning" sx={{ py: 0 }}>
              You have unsaved changes
            </Alert>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {hasUnsavedChanges && (
            <Button 
              variant="outlined" 
              onClick={() => {
                // Reset all local states to original values
                if (hasUnsavedThemeChanges) {
                  setThemeMode(savedMode); // Revert theme to saved state
                }
                // Reset other states...
                setNotificationsChanged(false);
                setProfileChanged(false);
                setPreferencesChanged(false);
                setHasUnsavedChanges(false);
                console.log('🔄 Settings: All changes discarded');
              }}
              disabled={isSaving}
            >
              Discard Changes
            </Button>
          )}
          
          <Button
            variant="contained"
            size="large"
            onClick={handleSaveAllChanges}
            disabled={isSaving || !hasUnsavedChanges}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              minWidth: 150,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1.1rem',
              py: 1.5
            }}
          >
            {isSaving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </Box>
      </Box>

      {/* Export Data Dialog */}
      <Dialog 
        open={exportDataDialog} 
        onClose={() => setExportDataDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Export Your Data</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Choose the format for your data export:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleExportData('json')}
              sx={{ height: 80, flexDirection: 'column' }}
            >
              <Download sx={{ mb: 1 }} />
              JSON Format
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleExportData('csv')}
              sx={{ height: 80, flexDirection: 'column' }}
            >
              <Download sx={{ mb: 1 }} />
              CSV Format
            </Button>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Your export will include profile data, loyalty programs, preferences, and transaction history.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDataDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Notification Snackbar */}
      <Snackbar
        open={notificationState.open}
        autoHideDuration={4000}
        onClose={() => setNotificationState(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setNotificationState(prev => ({ ...prev, open: false }))}
          severity={notificationState.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notificationState.message}
        </Alert>
      </Snackbar>

    </Container>
    </>
  );
};